import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import Counter from "../../../components/base/Counter";

export default function AboutSection() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    const element = document.getElementById("about");
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  const stats = [
    { number: "500+", label: "Projects Completed", icon: "ri-building-line" },
    { number: "50+", label: "Premium Clients", icon: "ri-user-star-line" },
    { number: "15+", label: "Years Experience", icon: "ri-time-line" },
    { number: "98%", label: "Client Satisfaction", icon: "ri-heart-line" },
  ];

  const features = [
    {
      icon: "ri-palette-line",
      title: "Design & Consultancies / Design & Build Projects",
      description:
        "We offer creative design consultancy and design-build solutions that blend functionality and style, delivering spaces that perfectly reflect your vision.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Trading – Premium Materials Supply",
      description:
        "Prime Edge supplies premium materials for architectural and interior projects, including wood, veneer, decorative metals, marble, WPC fluted panels, mirrors, glass partitions, and more — ensuring top quality for modern design and construction needs.",
    },
    {
      icon: "Expert Support",
      title: "Contracting – Turnkey Project Execution",
      description:
        "We deliver turnkey contracting solutions across civil works, MEP, and finishing, ensuring smooth execution, top-quality results, and timely completion with unmatched craftsmanship.",
    },
    {
      icon: "",
      title: "",
      description:
        "At Prime Edge, we are driven by a commitment to quality, innovation, and customer satisfaction. Every project we undertake is a reflection of our passion for excellence and our promise to deliver beyond expectations.",
    },
  ];

  return (
    <section
      id="about"
      className="relative py-20 overflow-hidden bg-gradient-to-br from-[#f5f5f5] via-[#ffffff] to-[#e5e5e5]"
    >
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 right-20 w-80 h-80 bg-gradient-to-br from-[#225a96]/10 to-[#d02b37]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-64 h-64 bg-gradient-to-tr from-[#d02b37]/8 to-[#225a96]/10 rounded-full blur-2xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-radial from-[#225a96]/5 via-transparent to-transparent rounded-full" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 0.2 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isVisible ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#225a96]/10 to-[#d02b37]/10 rounded-full mb-6 backdrop-blur-sm border border-[#225a96]/20"
          >
            <i className="ri-information-line text-lg text-[#d02b37] mr-2" />
            <span
              className="text-[#225a96] font-semibold text-sm tracking-wider uppercase"
              style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
            >
              About Prime Edge
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: 0.4 }}
            className="text-3xl md:text-5xl font-bold text-[#225a96] mb-6 leading-tight"
            style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
          >
             Crafting Excellence in
            <span className="block bg-gradient-to-r from-[#d02b37] to-[#ff6b7a] bg-clip-text text-transparent">
              Workspace Design
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: 0.6 }}
            className="text-base md:text-lg text-[#1a4670] max-w-3xl mx-auto leading-relaxed"
            style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
          >
           PRIME EDGE is a premier partner to the construction market across the middle east & gulf region.  Established in Beirut in 2024, we have undertaken several challenging projects, delivering professional and high-quality fit-out services to contractors and project owners.
          </motion.p>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, delay: 0.8 }}
            className="space-y-6"
          >
            <div className="space-y-4">
              <motion.h3
                initial={{ opacity: 0, y: 30 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 1 }}
                className="text-xl md:text-2xl font-bold text-[#225a96] leading-tight"
                style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
              >
                Where Innovation Meets Elegance
              </motion.h3>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 1.2 }}
                className="text-sm text-[#1a4670] leading-relaxed"
                style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
              >
                Our multidisciplinary team of designers, engineers, and craftsmen work collaboratively to transform ideas into reality — whether it’s creating striking interiors, 
                supplying top-quality materials, or executing complete turnkey projects.
              </motion.p>

             
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-2 gap-4 mt-8">
              {features.map((feature, index) => (
                <motion.div
                  layout
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isVisible ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 1 + index * 0.2 }}
                  whileHover={{
                    scale: 1.05,
                    backgroundColor: "rgba(34, 90, 150, 0.05)",
                    boxShadow: "0 20px 40px rgba(34, 90, 150, 0.15)",
                    transition: { type: "spring", stiffness: 300, damping: 25 }, // immediate spring
                  }}
                  style={{ pointerEvents: "auto" }}
                  className="p-4 rounded-2xl bg-white/80 backdrop-blur-sm border border-[#e5e5e5] hover:border-[#225a96]/30 transition-all duration-500 group cursor-pointer"
                >
                  <motion.i
                    className={`${feature.icon} text-xl text-[#d02b37] mb-3 block`}
                    whileHover={{ scale: 1.08 }}
                    transition={{ duration: 0.6 }}
                  />
                  <h4
                    className="text-base font-bold text-[#225a96] mb-2"
                    style={{
                      fontFamily: "system-ui, -apple-system, sans-serif",
                    }}
                  >
                    {feature.title}
                  </h4>
                  <p
                    className="text-xs text-[#1a4670] leading-relaxed group-hover:text-[#225a96] transition-colors duration-300"
                    style={{
                      fontFamily: "system-ui, -apple-system, sans-serif",
                    }}
                  >
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div> 
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, delay: 1 }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-2xl shadow-2xl group">
              <motion.img
                src="https://readdy.ai/api/search-image?query=Modern%20luxury%20office%20workspace%20with%20premium%20furniture%2C%20executive%20desk%2C%20ergonomic%20chair%2C%20contemporary%20design%2C%20professional%20lighting%2C%20clean%20minimalist%20aesthetic%2C%20high-end%20materials%2C%20sophisticated%20business%20environment%2C%20elegant%20interior%20design%2C%20productivity-focused%20layout&width=600&height=500&seq=about-office-1&orientation=landscape"
                alt="Premium Office Workspace"
                className="w-full h-[500px] object-cover object-top transition-transform duration-700 group-hover:scale-110"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.8 }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#225a96]/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              {/* Floating Elements */}
              <motion.div
                className="absolute top-6 right-6 bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-xl"
                initial={{ opacity: 0, scale: 0.7 }}
                animate={isVisible ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.8, delay: 1.5 }}
                whileHover={{ scale: 1.1, rotate: 5 }}
              >
                <i className="ri-award-line text-xl text-[#d02b37] mb-1 block" />
                <div
                  className="text-lg font-bold text-[#225a96]"
                  style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
                >
                  15+
                </div>
                <div
                  className="text-xs text-[#1a4670] font-medium"
                  style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
                >
                  Years
                </div>
              </motion.div>

              <motion.div
                className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-xl"
                initial={{ opacity: 0, scale: 0.7 }}
                animate={isVisible ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.8, delay: 1.7 }}
                whileHover={{ scale: 1.1, rotate: -5 }}
              >
                <i className="ri-user-star-line text-xl text-[#d02b37] mb-1 block" />
                <div
                  className="text-lg font-bold text-[#225a96]"
                  style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
                >
                  50+
                </div>
                <div
                  className="text-xs text-[#1a4670] font-medium"
                  style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
                >
                  Clients
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6"
        >
          {stats.map((stat, index) => {
            const { ref, inView } = useInView({
              triggerOnce: true,
              threshold: 0.3,
            });

            return (
              <motion.div
                key={index}
                ref={ref}
                initial={{ opacity: 0, scale: 0.7, y: 40 }}
                animate={isVisible ? { opacity: 1, scale: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 2.2 + index * 0.2 }}
                whileHover={{
                  scale: 1.08,
                }}
                className="text-center p-6 rounded-2xl    transition-all duration-500 group cursor-pointer"
              >
                <motion.i
                  className={`${stat.icon} text-4xl text-[#d02b37] mb-4 block`}
                  whileHover={{ scale: 1.3 }}
                  transition={{ duration: 0.7 }}
                />
                <motion.div
                  className="text-2xl md:text-3xl font-bold text-[#225a96] mb-3"
                  style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
                  whileHover={{ scale: 1.15 }}
                >
                  {/* Only render Counter when the card is in view */}
                  {inView&&<Counter
                    target={stat.number}
                    duration={2500}
                    delay={800 + index * 200} // <-- delay counter start
                  />}
                </motion.div>
                <div
                  className="text-[#1a4670] font-medium uppercase tracking-wider text-xs group-hover:text-[#225a96] transition-colors duration-300"
                  style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
                >
                  {stat.label}
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
